import React, { useEffect, useState, Suspense, useRef, memo } from "react";
import { useNavigate } from "react-router-dom";
import { useFadeInScroll } from "../../../components/Animation/fadeinScroll";
import { Canvas, useLoader, useFrame } from "@react-three/fiber";
import { OrbitControls, Center, Html } from "@react-three/drei";
import { STLLoader } from "three-stdlib";
import * as THREE from "three";

// 👉 import your carousel images
import { carouselImageSet } from "../../../assets/images/castsImages/carousel";

// ==========================================
// MODEL
// ==========================================
const Model = memo(({ geometries, reverseSpin }: any) => {
  const [geometryA, geometryB, geometryC] = geometries;
  const groupRef = useRef<THREE.Group>(null);

  const speedRef = useRef(reverseSpin ? -30 : 30);
  const dropOffset = useRef(60);

  useFrame((_, delta) => {
    if (groupRef.current) {
      const targetSpeed = reverseSpin ? -0.15 : 0.15;

      speedRef.current = THREE.MathUtils.lerp(speedRef.current, targetSpeed, delta * 2);
      groupRef.current.rotation.y += speedRef.current * delta;

      dropOffset.current = THREE.MathUtils.lerp(dropOffset.current, 0, delta * 3.5);
      groupRef.current.position.y = dropOffset.current;
    }
  });

  return (
    <group ref={groupRef} scale={[0.15, 0.15, 0.15]}>
      <Center>
        <mesh geometry={geometryA} rotation={[Math.PI, 0, 0]}>
          <meshStandardMaterial color="#f9fafb" roughness={0.35} metalness={0.15} />
        </mesh>
        <mesh geometry={geometryC} rotation={[Math.PI, 0, 0]}>
          <meshStandardMaterial
            color="#2563eb"   // 👈 give different color to highlight ORing
            roughness={0.25}
            metalness={0.4}
          />
        </mesh>
        <mesh geometry={geometryB} rotation={[Math.PI, 0, 0]}>
          <meshStandardMaterial color="#f9fafb" roughness={0.35} metalness={0.15} />
        </mesh>
      </Center>
    </group>
  );
});

// ==========================================
// VIEWER
// ==========================================
const Viewer = ({ geometries, reverseSpin = false }: any) => {
  return (
    <div className="hidden lg:block w-[550px] h-[700px] pointer-events-none">
      <Canvas camera={{ position: [0, 0, 60], fov: 45 }}>
        <Suspense
          fallback={
            <Html center>
              <div className="text-sm text-gray-500">Loading 3D...</div>
            </Html>
          }
        >
          <ambientLight intensity={0.7} />
          <directionalLight position={[10, 10, 5]} intensity={1.2} />
          <directionalLight position={[-10, -5, -5]} intensity={0.4} />

          <Model geometries={geometries} reverseSpin={reverseSpin} />
        </Suspense>

        <OrbitControls enableZoom={false} enablePan={false} />
      </Canvas>
    </div>
  );
};

// ==========================================
// MOBILE CAROUSEL
// ==========================================
const MobileCarousel = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % carouselImageSet.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="lg:hidden relative flex items-center justify-center min-h-[300px]">
      {carouselImageSet.map((img, index) => {
        const offset = index - activeIndex;

        if (Math.abs(offset) > 1) return null;

        return (
          <img
            key={index}
            src={img}
            className="absolute w-full max-w-[300px] transition-all duration-700"
            style={{
              transform: `translateX(${offset * 50}px) scale(${1 - Math.abs(offset) * 0.1})`,
              opacity: offset === 0 ? 1 : 0,
              zIndex: 10 - Math.abs(offset),
            }}
          />
        );
      })}

      {/* dots */}
      <div className="absolute -bottom-6 flex gap-2">
        {carouselImageSet.map((_, i) => (
          <button
            key={i}
            onClick={() => setActiveIndex(i)}
            className={`h-2 w-2 rounded-full ${i === activeIndex ? "bg-gray-900" : "bg-gray-300"}`}
          />
        ))}
      </div>
    </div>
  );
};

// ==========================================
// MAIN
// ==========================================
const NormalRotate = () => {
  const navigate = useNavigate();

  const leftRef = useFadeInScroll<HTMLDivElement>();
  const centerRef = useFadeInScroll<HTMLDivElement>();
  const rightRef = useFadeInScroll<HTMLDivElement>();

  const stlUrlA = "/models/newV4.stl";
  const stlUrlB = "/models/newV4.stl";
  const stlUrlC = "/models/Orings.stl";

const geometries = useLoader(STLLoader, [stlUrlA, stlUrlB, stlUrlC]) as [
  THREE.BufferGeometry,
  THREE.BufferGeometry,
  THREE.BufferGeometry
];

  return (
    <section className="relative min-h-[90svh] flex items-center overflow-hidden bg-white">
      <div className="absolute inset-0 pointer-events-none opacity-[0.035] bg-[linear-gradient(to_right,#000_1px,transparent_1px),linear-gradient(to_bottom,#000_1px,transparent_1px)] bg-[size:52px_52px]" />

      <div className="relative w-full max-w-8xl mx-auto px-6 sm:px-8 lg:px-10 grid grid-cols-1 lg:grid-cols-3 items-center gap-4 lg:gap-10">

        {/* LEFT (Desktop only) */}
        <div className="hidden lg:flex justify-end">
          <Viewer geometries={geometries} reverseSpin={false} />
        </div>

        {/* CENTER */}
        <div
          ref={centerRef.ref}
          className={`text-center space-y-6 z-10 transition-all duration-[1200ms] transform ${
            centerRef.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
          }`}
        >
          <h2 className="text-hero text-primary font-primary">YESOAIR</h2>

          <p className="text-gray-600 text-body max-w-md mx-auto">
            Replacing outdated casts with advanced, patient-friendly
            immobilizations that are breathable, waterproof, and built
            for modern life.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <button
              onClick={() => navigate("/products")}
              className="px-8 py-3 rounded-2xl bg-primary text-white"
            >
              Get Started
            </button>

            <button
              onClick={() => navigate("/products")}
              className="px-8 py-3 rounded-2xl border border-gray-300"
            >
              View Product
            </button>
          </div>

          <p className="text-sm text-gray-500 pt-3">
            CDSCO CERTIFIED – Sacrum-Pune-MH/M/MD/022990
          </p>

          {/* 👉 MOBILE CAROUSEL HERE */}
          <MobileCarousel />
        </div>

        {/* RIGHT (Desktop only) */}
        <div className="hidden lg:flex justify-start">
          <Viewer geometries={geometries} reverseSpin={true} />
        </div>

      </div>
    </section>
  );
};

export default NormalRotate;